import React, { useCallback, useEffect, useRef, useState } from 'react';
import '../../styles/components/_leads.scss';
import LeadProvider from '../../infra/provider/leadProvider';
import { ListSearch } from '../../domain/entities/valueObjects/listSearch';
import { toast } from 'react-toastify';
import { Formatter } from '../../utils/formatter';
import { debounce } from 'lodash';
import Tooltip from '../tootip/Tootip';
import { FilterX } from "lucide-react";

interface TableData {
  id: string;
  createdAt: string;
  name: string;
  document: string;
  email: string;
  phone: string;
  city: string;
  uf: string;
  street: string;
  monthlyIncome: string;
  patrimony: string;
  origin: string;
}

const Leads = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedOrigin, setSelectedOrigin] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const [data, setData] = useState<TableData[]>([]);
  const isFetching = useRef(false);
  const leadProvider = LeadProvider.getInstance();
  const [isLoading, setIsLoading] = useState(true);

  const getLeads = useCallback(
    async (
      pageNumber = 1,
      searchTerm = "",
      selectedDate = "",
      originFilter = "",
    ) => {
      if (isFetching.current) return;
      isFetching.current = true;
      setIsLoading(true);
      const search = new ListSearch({
        searchTerm: searchTerm,
        orderBy: "createdAt",
        limit: itemsPerPage,
        offset: (pageNumber - 1) * itemsPerPage,
        fromDate: selectedDate ? Formatter.dateStringToTimestamp(selectedDate) : undefined,
        toDate: selectedDate ? Formatter.dateStringToTimestamp(selectedDate) : undefined,
      });
      try {
        const response =
          await leadProvider.getLeads(search);
        let mappedData = response.leads.map((lead) => ({
          id: lead.id,
          createdAt: Formatter.timestampToDateBR(lead.createdAt),
          name: lead.name,
          document: lead.document,
          email: lead.email,
          phone: lead.phone,
          city: lead.address.city,
          uf: lead.address.uf,
          street: lead.address.street,
          monthlyIncome: Formatter.formatNumberToBRCurrency(lead.monthlyIncome),
          patrimony: Formatter.formatNumberToBRCurrency(lead.patrimony),
          origin: lead.source?.trim() || 'Homecorp'

        }));

        // Debug: mostrar todas as origens únicas
        const uniqueOrigins = [...new Set(mappedData.map(lead => lead.origin?.trim() || 'Homecorp'))];
        console.log('Unique origins found:', uniqueOrigins);
        console.log('Filtering for:', originFilter);

        // Aplicar filtro de origem se necessário
        let filteredData = mappedData;
        if (originFilter) {
          filteredData = mappedData.filter(lead => {
            // Normalizar origem: leads sem origem são Homecorp
            const leadOrigin = lead.origin?.trim() || 'Homecorp';
            // Comparar de forma case-insensitive para garantir compatibilidade
            const matches = leadOrigin.toLowerCase() === originFilter.toLowerCase();
            // Debug temporário
            if (originFilter === 'Homecorp') {
              console.log('Filtering for Homecorp. Lead origin:', leadOrigin, 'Lead ID:', lead.id, 'Matches:', matches);
            }
            return matches;
          });
          console.log('Filtered results:', filteredData.length, 'out of', mappedData.length);
        }

        // Aplicar paginação local
        const startIndex = (pageNumber - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedData = filteredData.slice(startIndex, endIndex);

        setData(paginatedData);
        setTotalItems(filteredData.length);
      } catch (error) {
        toast.error("Erro ao carregar visitas agendadas");
        console.error(error);
      } finally {
        isFetching.current = false;
        setIsLoading(false);
      }
    },
    [leadProvider, itemsPerPage]
  );

  useEffect(() => {
    getLeads(currentPage, searchTerm, selectedDate, selectedOrigin);
  }, [itemsPerPage, currentPage, searchTerm, selectedDate, selectedOrigin, getLeads]);

  const clearFilters = () => {
    if (searchTerm === "" && selectedDate === "" && selectedOrigin === "") return;
    setSearchTerm('');
    setSelectedDate('');
    setSelectedOrigin('');
    setCurrentPage(1);
    getLeads(1, "", "", "");
  };

  const formatPhone = (phone: string): string => {
    return Formatter.phoneToBR(phone);
  };


  const debouncedSearchTerm = useRef(
    debounce((term) => {
      getLeads(1, term, selectedDate, selectedOrigin);
    }, 600)
  ).current;

  const debouncedDate = useRef(
    debounce((date) => {
      getLeads(1, searchTerm, date, selectedOrigin);
    }, 600)
  ).current;

  const handleSearchTermChange = (searchTerm: string) => {
    setSearchTerm(searchTerm);
    debouncedSearchTerm(searchTerm);
  };

  const handleSearchDateChange = (date: string) => {
    setSelectedDate(date);
    debouncedDate(date);
  };

  const handleOriginChange = (origin: string) => {
    setSelectedOrigin(origin);
    setCurrentPage(1);
    getLeads(1, searchTerm, selectedDate, origin);
  };

  return (
    <div className="data-table-container-leads">
      <div className="filters-container-leads">
        <div className="search-container-leads">
          <input
            type="text"
            placeholder="Busque por nome, CPF, ID, etc..."
            value={searchTerm}
            onChange={(e) => handleSearchTermChange(e.target.value)}
          />

        </div>
        <select
          value={selectedOrigin}
          onChange={(e) => handleOriginChange(e.target.value)}
          className="status-select-leads"
        >
          <option value="">Todas as origens</option>
          <option value="Site">Site</option>
          <option value="Backoffice">Backoffice</option>
        </select>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => handleSearchDateChange(e.target.value)}
          className="date-input-leads"
        />
        <Tooltip text="Limpar filtros">
          <button className="clear-filters-button" onClick={clearFilters}>
            <FilterX size={16} />
          </button>
        </Tooltip>
      </div>

      <div className="table-wrapper-leads">
        <table className="modern-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Origem</th>
              <th>Data de criação</th>
              <th>Nome / CPF</th>
              <th>Email</th>
              <th>Telefone</th>
              <th>Localização</th>
              <th>Renda mensal</th>
              <th>Patrimônio</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                  Carregando...
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                  Nenhum resultado encontrado.
                </td>
              </tr>
            ) : (
              data.map((item, index) => (
                <tr key={index} className="table-row">
                  <td>{item.id}</td>
                  <td>
                    <span className={`origin-badge ${item.origin === 'Site' ? 'origin-site' : 'origin-homecorp'}`}>
                      {item.origin}
                    </span>
                  </td>
                  <td>{item.createdAt}</td>
                  <td>
                    <div style={{ fontWeight: 'bold' }}>{item.name}</div>
                    {item.document && (
                      <div style={{ fontSize: '0.9em', color: '#666' }}>
                        {item.document}
                      </div>
                    )}
                  </td>
                  <td>
                    {item.email ? (
                      item.email
                    ) : (
                      <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>
                        sem-email@homecorp.com
                      </span>
                    )}
                  </td>
                  <td>{formatPhone(item.phone)}</td>
                  <td>{`${item.city} - ${item.uf}`}</td>
                  <td>{item.monthlyIncome}</td>
                  <td>
                    {item.patrimony ? (
                      item.patrimony
                    ) : (
                      <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>
                        Não informado
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="table-footer-leads">
        <div className="items-per-page-leads">
          <span>Exibir por</span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={30}>30</option>
          </select>
        </div>
        <div className="pagination-leads">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Anterior
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={currentPage === page ? 'active' : ''}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Próximo
          </button>
        </div>
      </div>
    </div>

  );
};

export default Leads;
